import { Field, Formik } from 'formik';
import { DSButton } from '@demandstar/components/button';
import { Loader } from 'src/components/customcontrols';
import { ModalPopUp } from 'src/components/common/modals';
import { DeprecatedFormikRadio } from 'src/components/common/controls/inputs/DeprecatedFormikRadio';
import { renderDeprecatedTextField } from 'src/components/common/controls/inputs/text';
import DatepickerField from 'src/components/common/controls/inputs/datepicker';
import TimePicker from 'src/components/common/controls/inputs/timepicker';
import { CommodityMap } from 'src/types/commodity';
import { formikSelectAdapter } from 'src/utils/helpers/addInstantBidHelper';
import { InstantBidCommodityList } from './InstantBidCommodityList';
import { InstantBidSuppliersTable } from './InstantBidSuppliersTable';
import InstantBidManualCommoditySelection from '../InstantBidManualCommoditySelection';
import renderSelect from '../../../common/controls/inputs/selectbox';
import renderTextarea from '../../../common/controls/inputs/textarea';
import { validate } from 'src/components/common/createbid/form/validations';
import { scrollToTop } from 'src/utils/helpers';

interface InstantBidExtractedFormProps {
  formData: any;
  mergedCodes: any[];
  checkedCommodities: any[];
  isFetchingCommodityCodes: boolean;
  suppliers: any[];
  suppliersLoading: boolean;
  currentPage: number;
  supplierPerPage: number;
  selectedCommodityIds: any[];
  showAddcommodityPopUp: boolean;
  commoditydata: any[];
  onCheckboxChange: (commodity: any) => void;
  onAddManually: () => void;
  onPageChange: (page: number) => void;
  onFormSubmit: (values: any) => void;
  onManualCommodityProceed: (selected: CommodityMap[]) => void;
  onManualCommodityCancel: () => void;
  bidTypes: any;
  bidWriters: any;
  auth: any;
}

export function InstantBidExtractedForm({
  formData,
  mergedCodes,
  checkedCommodities,
  isFetchingCommodityCodes,
  suppliers,
  suppliersLoading,
  currentPage,
  supplierPerPage,
  selectedCommodityIds,
  showAddcommodityPopUp,
  commoditydata,
  onCheckboxChange,
  onAddManually,
  onPageChange,
  onFormSubmit,
  onManualCommodityProceed,
  onManualCommodityCancel,
  bidTypes,
  bidWriters,
  auth,
}: InstantBidExtractedFormProps) {
  const addBidWriters = bidWriters?.filter(item => item.ac === true) || [];
  const matchedBidType = bidTypes?.find(item => item.bidType === formData.bidType) || bidTypes?.[0];

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          eBidding: formData?.eBidding || 'yes',
          bidNumber: formData?.bidNumber || '',
          bidName: formData?.bidName || '',
          bidBond: formData?.bidBond || '',
          openedText: formData?.openedText || '',
          scopeOfWork: formData?.scopeOfWork || '',
          broadcastDate: formData?.broadcastDate || '',
          broadcastDateTime: formData?.broadcastDateTime || '',
          broadcastDateTimeZone: formData?.broadcastDateTimeZone || '',
          dueDate: formData?.dueDate || '',
          dueDateTime: formData?.dueDateTime || '',
          dueDateTimeZone: formData?.dueDateTimeZone || '',
          bidWriterAccountId: addBidWriters.find(item => item.value === auth?.accountId) || '',
          memberBidType: matchedBidType || '',
          projectEstimatedBudget: formData.projectEstimatedBudget || '',
        }}
        onSubmit={onFormSubmit}
        validate={validate}
      >
        {({ handleSubmit, setFieldValue, values ,validateForm }) => (
          <form onSubmit={async(e)=>{e.preventDefault();
            const errors = await validateForm();
            if(Object.keys(errors).length >0){
              scrollToTop();
            }
            handleSubmit(e);
          }}>
            <div className='row'>
              {values.eBidding && (
                <div className='col-md-12 mb-3'>
                  <div className='border rounded pt-4 px-3 pb-3 bg-light'>
                    <h6 className='mb-2'>E-Bidding</h6>

                    <Field
                      name='eBidding'
                      component={DeprecatedFormikRadio}
                      type='radio'
                      value='yes'
                      defaultChecked={values.eBidding === 'yes'}
                      title={[
                        <strong key={1}>Yes</strong>,
                        ', allow suppliers to submit responses electronically.',
                      ]}
                      label={[
                        <strong key={1}>Yes</strong>,
                        ', allow suppliers to submit responses electronically.',
                      ]}
                      parentClass='d-inline-block mr-4 mt-2'
                      handleSelect={(name: string, val: string) => setFieldValue(name, val)}
                    />

                    <Field
                      name='eBidding'
                      component={DeprecatedFormikRadio}
                      type='radio'
                      value='no'
                      defaultChecked={values.eBidding === 'no'}
                      title={[
                        <strong key={1}>No</strong>,
                        ', do not allow suppliers to submit responses.',
                      ]}
                      label={[
                        <strong key={1}>No</strong>,
                        ', do not allow suppliers to submit responses.',
                      ]}
                      parentClass='d-inline-block mt-2'
                      handleSelect={(name: string, val: string) => setFieldValue(name, val)}
                    />
                  </div>
                </div>
              )}

              <div className='col-md-6 mb-3'>
                <Field
                  label='Bid Number'
                  name='bidNumber'
                  component={renderDeprecatedTextField}
                  onChange={(name: string, value: any) => setFieldValue(name, value)}
                />
              </div>

              <div className='col-md-6 mb-3'>
                <Field
                  label='Bid Name'
                  name='bidName'
                  component={renderDeprecatedTextField}
                  onChange={(name: string, value: any) => setFieldValue(name, value)}
                />
              </div>

              <div className='col-md-6 mb-3'>
                <Field
                  label='Bid Bond'
                  name='bidBond'
                  component={renderDeprecatedTextField}
                  onChange={(name: string, value: any) => setFieldValue(name, value)}
                  optional
                />
              </div>
              <div className='col-md-6 mb-3'>
                <Field
                  label='Bid Type'
                  name='memberBidType'
                  options={bidTypes}
                  component={renderSelect}
                  onChange={setFieldValue}
                />
              </div>

              <div className='col-md-6 mb-3'>
                <Field
                  label='Opened Text'
                  name='openedText'
                  component={renderTextarea}
                  onChange={(name: string, value: any) => setFieldValue(name, value)}
                />
              </div>

              <div className='col-md-6 mb-3'>
                <Field
                  label='Scope Of Work'
                  name='scopeOfWork'
                  component={renderTextarea}
                  onChange={(name: string, value: any) => setFieldValue(name, value)}
                />
              </div>

              <div className='col-md-6 mb-3'>
                <Field
                  name='bidWriterAccountId'
                  label='Bid Writer'
                  type='select'
                  component={renderSelect}
                  options={addBidWriters}
                  onChange={setFieldValue}
                />
              </div>
              <div className='col-md-6 mb-3'>
                <Field
                  label='Project Estimated Budget'
                  name='projectEstimatedBudget'
                  placeholder='Hidden from Suppliers'
                  component={renderDeprecatedTextField}
                  onChange={(name: string, value: any) => setFieldValue(name, value)}
                  optional
                />
              </div>

              <div className='col-md-12 mb-3'>
                <div className='row'>
                  <div className='col-md-6'>
                    <Field
                      label='Broadcast Date'
                      name='broadcastDate'
                      component={DatepickerField}
                      onChange={(value: any) => setFieldValue('broadcastDate', value)}
                      parentClass='my-1'
                      innerClass='dateBoxAlignment'
                    />
                  </div>

                  <div className='col-md-6 mt-1'>
                    <TimePicker
                      component={formikSelectAdapter}
                      timeFieldName='broadcastDateTime'
                      zoneFieldName='broadcastDateTimeZone'
                      label='Time'
                      setFieldValue={(fieldName: string, e: any) => {
                        setFieldValue(fieldName, e?.target ? e.target.value : e);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='col-md-12 mb-3'>
                <div className='row'>
                  <div className='col-md-6'>
                    <Field
                      label='Due Date'
                      name='dueDate'
                      component={DatepickerField}
                      onChange={(value: any) => setFieldValue('dueDate', value)}
                      parentClass='my-1'
                      innerClass='dateBoxAlignment'
                    />
                  </div>

                  <div className='col-md-6 mt-1'>
                    <TimePicker
                      component={formikSelectAdapter}
                      timeFieldName='dueDateTime'
                      zoneFieldName='dueDateTimeZone'
                      label='Time'
                      setFieldValue={(fieldName: string, e: any) => {
                        setFieldValue(fieldName, e?.target ? e.target.value : e);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {isFetchingCommodityCodes ? (
              <Loader loading={isFetchingCommodityCodes} />
            ) : (
              <div className='row'>
                <div className='col-md-6 col-sm-12'>
                  <InstantBidCommodityList
                    mergedCodes={mergedCodes}
                    checkedCommodities={checkedCommodities}
                    onCheckboxChange={onCheckboxChange}
                    onAddManually={onAddManually}
                  />
                </div>

                <div className='col-md-6 col-sm-12'>
                  <InstantBidSuppliersTable
                    suppliers={suppliers}
                    loading={suppliersLoading}
                    currentPage={currentPage}
                    supplierPerPage={supplierPerPage}
                    selectedCommodityIds={selectedCommodityIds}
                    onPageChange={onPageChange}
                  />
                </div>
              </div>
            )}

            <div className='text-right mt-3'>
              <DSButton type='submit'>Proceed To Create Bid</DSButton>
            </div>
          </form>
        )}
      </Formik>

      <ModalPopUp
        title='Add Commodity Codes Manually'
        closeModal={onManualCommodityCancel}
        isOpen={showAddcommodityPopUp}
        backdrop='static'
        size='xxl'
      >
        <InstantBidManualCommoditySelection
          allCommodities={commoditydata}
          preSelectedCommodities={checkedCommodities.map(c => {
            const formattedCode = c.formattedCode || c.fullcode || '';
            const codeParts = formattedCode.split('-');
            return {
              commodityId: c.commodityid,
              commodityDescription: c.commodity,
              fullCode: c.fullcode,
              formattedCode,
              commodityCode: codeParts[2] ?? formattedCode.slice(-2),
              isSelected: true,
            } as CommodityMap;
          })}
          onProceed={onManualCommodityProceed}
          onCancel={onManualCommodityCancel}
        />
      </ModalPopUp>
    </>
  );
}
